import { where, Timestamp, orderBy, arrayUnion, doc, updateDoc } from 'firebase/firestore';
import { FirestoreService, type AuditContext } from '@/lib/firestore';
import { db } from '@/lib/firebase';
import { COLLECTION_NAMES } from '@/lib/collections';
import { Logger } from '@/lib/logger';
import { activityEventService } from '@/shared/services/activityEventService';
import type {
  HiringSpot,
  CreateHiringSpotData,
  UpdateHiringSpotData,
  AddConversationEntryData,
  ConversationEntry,
  ApprovalHistoryEntry,
  ConversationMessageType,
  ApprovalStatus,
} from '@/shared/types/hiringSpot';
import {
  CreateHiringSpotDataSchema,
  UpdateHiringSpotDataSchema,
  AddConversationEntryDataSchema,
} from '@/shared/types/hiringSpot';

const logger = Logger.createScoped('HiringSpotService');

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Build comprehensive field changes array from hiring spot data
 * Filters out undefined values and handles arrays/nulls properly
 */
function buildHiringSpotFieldChanges(
  newSpot: Partial<HiringSpot> | CreateHiringSpotData,
  oldSpot?: HiringSpot
): Array<{ field_name: string; old_value: any; new_value: any; display_name: string }> {
  const fieldChanges: Array<{ field_name: string; old_value: any; new_value: any; display_name: string }> = [];

  // Helper to add field change if value is defined
  const addFieldChange = (
    fieldName: keyof HiringSpot | keyof CreateHiringSpotData,
    displayName: string,
    newValue: any,
    oldValue: any = null
  ) => {
    if (newValue !== undefined) {
      fieldChanges.push({
        field_name: fieldName as string,
        old_value: oldValue,
        new_value: newValue,
        display_name: displayName,
      });
    }
  };

  // Core fields
  addFieldChange('role', 'Role', (newSpot as any).role, oldSpot?.role);
  addFieldChange('confidence', 'Confidence Level', (newSpot as any).confidence, oldSpot?.confidence);
  addFieldChange('priority', 'Priority Level', (newSpot as any).priority, oldSpot?.priority);

  // Team assignment fields
  addFieldChange('hiring_manager', 'Hiring Manager', (newSpot as any).hiring_manager, oldSpot?.hiring_manager);
  addFieldChange('trainer', 'Trainer', (newSpot as any).trainer, oldSpot?.trainer);
  addFieldChange('collaborators', 'Collaborators', (newSpot as any).collaborators, oldSpot?.collaborators);
  addFieldChange('workflows', 'Workflows', (newSpot as any).workflows, oldSpot?.workflows);

  // Hiring criteria
  addFieldChange('channel_background', 'Channel Background', (newSpot as any).channel_background, oldSpot?.channel_background);
  addFieldChange('vertical_background', 'Vertical Background', (newSpot as any).vertical_background, oldSpot?.vertical_background);
  addFieldChange('location', 'Location Preference', (newSpot as any).location, oldSpot?.location);
  addFieldChange('impact', 'Impact Areas', (newSpot as any).impact, oldSpot?.impact);
  addFieldChange('available_start_date', 'Available Start Date', (newSpot as any).available_start_date, oldSpot?.available_start_date);
  addFieldChange('forecasted_start_date', 'Forecasted Start Date', (newSpot as any).forecasted_start_date, oldSpot?.forecasted_start_date);

  // Context notes - check if they're provided in CreateHiringSpotData or in notes object
  const teamStrengths = (newSpot as any).team_strengths ?? (newSpot as any).notes?.team_strengths;
  const desiredSkills = (newSpot as any).desired_skills ?? (newSpot as any).notes?.desired_skills;
  const workflowResponsibilities = (newSpot as any).workflow_responsibilities ?? (newSpot as any).notes?.workflow_responsibilities;
  const teamPlanningImpact = (newSpot as any).team_planning_impact ?? (newSpot as any).notes?.team_planning_impact;
  const additionalContext = (newSpot as any).additional_context ?? (newSpot as any).notes?.additional_context;

  if (teamStrengths !== undefined) {
    addFieldChange('team_strengths', 'Team Strengths', teamStrengths, oldSpot?.notes?.team_strengths);
  }
  if (desiredSkills !== undefined) {
    addFieldChange('desired_skills', 'Desired Skills', desiredSkills, oldSpot?.notes?.desired_skills);
  }
  if (workflowResponsibilities !== undefined) {
    addFieldChange('workflow_responsibilities', 'Workflow Responsibilities', workflowResponsibilities, oldSpot?.notes?.workflow_responsibilities);
  }
  if (teamPlanningImpact !== undefined) {
    addFieldChange('team_planning_impact', 'Team Planning Impact', teamPlanningImpact, oldSpot?.notes?.team_planning_impact);
  }
  if (additionalContext !== undefined) {
    addFieldChange('additional_context', 'Additional Context', additionalContext, oldSpot?.notes?.additional_context);
  }

  return fieldChanges;
}

// ============================================================================
// HIRING SPOT SERVICE
// ============================================================================

/**
 * HiringSpotService - Service for managing hiring spots
 *
 * Features:
 * - CRUD operations for hiring spots
 * - Approval workflow management (submit, approve, archive)
 * - Conversation management
 * - Strong typing throughout
 * - Uses existing FirestoreService pattern
 * - Comprehensive error handling and logging
 *
 * Follows the same architectural patterns as workflowSpotService
 */
class HiringSpotService {
  private firestoreService: FirestoreService<HiringSpot>;

  constructor() {
    this.firestoreService = new FirestoreService<HiringSpot>(COLLECTION_NAMES.HIRING_SPOTS, {
      enableAudit: true,
    });
    logger.info('HiringSpotService initialized');
  }

  // ============================================================================
  // CRUD OPERATIONS
  // ============================================================================

  /**
   * Create a new hiring spot
   */
  async createHiringSpot(
    data: CreateHiringSpotData,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<string> {
    try {
      logger.info('Creating hiring spot', { role: data.role, team_id: data.team_id });

      // Validate input data
      const validatedData = CreateHiringSpotDataSchema.parse(data);

      const now = Timestamp.now();

      // Generate conversation entry ID
      const conversationEntryId = `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`;

      // Create initial conversation entry
      const initialConversationEntry: ConversationEntry = {
        id: conversationEntryId,
        author_id: userId,
        author_name: userName,
        message: 'Hiring spot created',
        timestamp: now,
        message_type: 'status_change' as ConversationMessageType,
        metadata: {
          new_status: 'pending_approval' as ApprovalStatus,
        },
      };

      const hiringSpot: Omit<HiringSpot, 'id'> = {
        // Core hiring information
        hiring_manager: validatedData.hiring_manager,
        trainer: validatedData.trainer ?? null,
        collaborators: validatedData.collaborators ?? [],
        workflows: validatedData.workflows ?? [],

        // Role and team context
        role: validatedData.role,
        team_id: validatedData.team_id,
        team_name: validatedData.team_name,
        program_id: validatedData.program_id,
        program_name: validatedData.program_name,
        department: validatedData.department,

        // Hiring criteria
        confidence: validatedData.confidence,
        priority: validatedData.priority,
        channel_background: validatedData.channel_background,
        vertical_background: validatedData.vertical_background,
        location: validatedData.location,
        impact: validatedData.impact,
        available_start_date: validatedData.available_start_date ?? null,
        forecasted_start_date: validatedData.forecasted_start_date ?? null,

        // Placement relationship
        confirmed_hire_id: null,

        // Context notes
        notes: {
          team_strengths: validatedData.team_strengths ?? null,
          desired_skills: validatedData.desired_skills ?? null,
          workflow_responsibilities: validatedData.workflow_responsibilities ?? null,
          team_planning_impact: validatedData.team_planning_impact ?? null,
          additional_context: validatedData.additional_context ?? null,
          conversation_history: [initialConversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
        },

        // Approval workflow
        approval_status: 'pending_approval',
        is_archived: false,
        is_closed: false,
        approval_metadata: {
          approval_history: [],
        },

        // Audit fields
        created_at: now,
        updated_at: now,
        created_by: userId,
        created_by_name: userName,
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: 1,

        // Metadata - only include if provided to avoid Firestore undefined errors
        ...(validatedData.metadata && { metadata: validatedData.metadata }),
      };

      const spotId = await this.firestoreService.create(hiringSpot, {
        userId,
        userName,
        action: 'create',
        resourceType: 'hiring_spot',
        resourceId: '',
        cycleId: auditContext.cycleId,
      });

      logger.info('Hiring spot created successfully', { spotId, role: data.role });

      // Emit activity event for hiring spot creation
      try {
        const visibility = validatedData.program_id ? 'program' : 'team';

        // Build comprehensive field changes for creation
        const fieldChanges = buildHiringSpotFieldChanges(validatedData);

        // Build affected user IDs - include creator, hiring manager, trainer, and collaborators
        const affectedUserIds = [
          userId,
          validatedData.hiring_manager,
          ...(validatedData.trainer ? [validatedData.trainer] : []),
          ...(validatedData.collaborators ?? []),
        ].filter((id, index, self) => id && self.indexOf(id) === index); // Remove duplicates and nulls

        await activityEventService.emit({
          domain: 'workflow',
          event_type: 'hiring_spot.created',
          actor: {
            user_id: userId,
            user_name: userName,
          },
          subject: {
            resource_type: 'hiring_spot',
            resource_id: spotId,
            resource_name: validatedData.role,
          },
          message: `Created '${validatedData.role}' hiring spot in ${validatedData.team_name || 'team'}`,
          context: {
            program_id: validatedData.program_id,
            program_name: validatedData.program_name,
            team_id: validatedData.team_id,
            team_name: validatedData.team_name,
          },
          change: {
            action: 'created',
            fields_changed: fieldChanges,
          },
          metadata: {
            affected_user_ids: affectedUserIds,
            affected_workflow_ids: validatedData.workflows ?? [],
            affected_employee_ids: [],
            tags: ['workflow', 'hiring_spot', 'created', 'capacity'],
            visibility: visibility,
          },
        });
      } catch (eventError) {
        logger.error('Failed to emit hiring_spot.created event', eventError, { spotId });
        // Don't throw - event emission failure shouldn't break the main operation
      }

      return spotId;

    } catch (error) {
      logger.error('Failed to create hiring spot', error, { role: data.role });
      throw error;
    }
  }

  /**
   * Get a hiring spot by ID
   */
  async getHiringSpot(spotId: string): Promise<HiringSpot | null> {
    try {
      const spot = await this.firestoreService.get(spotId);
      return spot;
    } catch (error) {
      logger.error('Failed to get hiring spot', error, { spotId });
      throw error;
    }
  }

  /**
   * Get hiring spots by team ID
   */
  async getHiringSpotsByTeam(teamId: string): Promise<HiringSpot[]> {
    try {
      const spots = await this.firestoreService.list([
        where('team_id', '==', teamId),
        orderBy('created_at', 'desc')
      ]);

      return spots;
    } catch (error) {
      logger.error('Failed to get hiring spots by team', error, { teamId });
      throw error;
    }
  }

  /**
   * Get hiring spots by program ID
   */
  async getHiringSpotsByProgram(programId: string): Promise<HiringSpot[]> {
    try {

      const spots = await this.firestoreService.list([
        where('program_id', '==', programId),
        orderBy('created_at', 'desc')
      ]);

      return spots;
    } catch (error) {
      logger.error('Failed to get hiring spots by program', error, { programId });
      throw error;
    }
  }

  /**
   * Get hiring spots by hiring manager
   */
  async getHiringSpotsByManager(managerId: string): Promise<HiringSpot[]> {
    try {

      const spots = await this.firestoreService.list([
        where('hiring_manager', '==', managerId),
        orderBy('created_at', 'desc')
      ]);

      return spots;
    } catch (error) {
      logger.error('Failed to get hiring spots by manager', error, { managerId });
      throw error;
    }
  }

  /**
   * Get all hiring spots
   */
  async getAllHiringSpots(): Promise<HiringSpot[]> {
    try {
      const spots = await this.firestoreService.list([
        orderBy('created_at', 'desc')
      ]);

      return spots;
    } catch (error) {
      logger.error('Failed to get all hiring spots', error);
      logger.error('Failed to get all hiring spots', error);
      throw error;
    }
  }

  /**
   * Update a hiring spot
   */
  async updateHiringSpot(
    spotId: string,
    updates: UpdateHiringSpotData,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      logger.info('Updating hiring spot', { spotId, updates: Object.keys(updates) });

      // Validate input data
      const validatedUpdates = UpdateHiringSpotDataSchema.parse(updates);

      // Get current spot to increment version
      const currentSpot = await this.firestoreService.getRequired(spotId);

      // Create update data with proper typing
      const updateData: Partial<Omit<HiringSpot, 'id'>> = {
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      // Add validated updates to the update data
      if (validatedUpdates.hiring_manager !== undefined) {
        updateData.hiring_manager = validatedUpdates.hiring_manager;
      }
      if (validatedUpdates.trainer !== undefined) {
        updateData.trainer = validatedUpdates.trainer;
      }
      if (validatedUpdates.collaborators !== undefined) {
        updateData.collaborators = validatedUpdates.collaborators;
      }
      if (validatedUpdates.workflows !== undefined) {
        updateData.workflows = validatedUpdates.workflows;
      }
      if (validatedUpdates.role !== undefined) {
        updateData.role = validatedUpdates.role;
      }
      if (validatedUpdates.confidence !== undefined) {
        updateData.confidence = validatedUpdates.confidence;

        // Append previous confidence value to history if it actually changed
        if (currentSpot.confidence !== validatedUpdates.confidence) {
          const spotRef = doc(db, COLLECTION_NAMES.HIRING_SPOTS, spotId);
          await updateDoc(spotRef, {
            confidence_history: arrayUnion({
              previous_value: currentSpot.confidence,
              changed_at: Timestamp.now(),
              changed_by: userId,
            }),
          });
        }
      }
      if (validatedUpdates.priority !== undefined) {
        updateData.priority = validatedUpdates.priority;
      }
      if (validatedUpdates.channel_background !== undefined) {
        updateData.channel_background = validatedUpdates.channel_background;
      }
      if (validatedUpdates.vertical_background !== undefined) {
        updateData.vertical_background = validatedUpdates.vertical_background;
      }
      if (validatedUpdates.location !== undefined) {
        updateData.location = validatedUpdates.location;
      }
      if (validatedUpdates.impact !== undefined) {
        updateData.impact = validatedUpdates.impact;
      }
      if (validatedUpdates.available_start_date !== undefined) {
        updateData.available_start_date = validatedUpdates.available_start_date;
      }
      if (validatedUpdates.forecasted_start_date !== undefined) {
        updateData.forecasted_start_date = validatedUpdates.forecasted_start_date;
      }
      if (validatedUpdates.metadata !== undefined) {
        updateData.metadata = validatedUpdates.metadata;
      }

      // Handle context notes field updates (stored under the notes sub-object)
      const notesFieldKeys = ['team_strengths', 'desired_skills', 'workflow_responsibilities', 'team_planning_impact', 'additional_context'] as const;
      const hasNotesUpdate = notesFieldKeys.some(key => validatedUpdates[key] !== undefined);
      if (hasNotesUpdate) {
        updateData.notes = { ...currentSpot.notes };
        for (const key of notesFieldKeys) {
          if (validatedUpdates[key] !== undefined) {
            updateData.notes[key] = validatedUpdates[key] as string | null;
          }
        }
      }

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'hiring_spot',
        resourceId: spotId,
        changes: updates,
        cycleId: auditContext.cycleId,
      });

      logger.info('Hiring spot updated successfully', { spotId });

      // Emit activity event for hiring spot update
      try {
        // Build comprehensive field changes using helper function
        const fieldChanges = buildHiringSpotFieldChanges(validatedUpdates, currentSpot);

        if (fieldChanges.length === 0) return;

        // Ensure collapsed view context fields (Role, Confidence, Available Start Date) are always included
        // even if they haven't changed, so the activity card can display current values
        const ensureField = (fieldName: string, displayName: string, currentValue: any) => {
          if (!fieldChanges.find(f => f.field_name === fieldName)) {
            fieldChanges.push({
              field_name: fieldName,
              old_value: currentValue,
              new_value: currentValue,
              display_name: displayName
            });
          }
        };

        ensureField('role', 'Role', currentSpot.role);
        ensureField('confidence', 'Confidence Level', currentSpot.confidence);
        ensureField('priority', 'Priority Level', currentSpot.priority);
        ensureField('available_start_date', 'Available Start Date', currentSpot.available_start_date);

        // Build affected user IDs - include updater, hiring manager, trainer, and collaborators
        const affectedUserIds = [
          userId,
          currentSpot.hiring_manager,
          ...(currentSpot.trainer ? [currentSpot.trainer] : []),
          ...(currentSpot.collaborators ?? []),
          // If hiring manager or trainer changed, include the new ones
          ...(validatedUpdates.hiring_manager ? [validatedUpdates.hiring_manager] : []),
          ...(validatedUpdates.trainer ? [validatedUpdates.trainer] : []),
          ...(validatedUpdates.collaborators ?? []),
        ].filter((id, index, self) => id && self.indexOf(id) === index); // Remove duplicates and nulls

        const visibility = currentSpot.program_id ? 'program' : 'team';

        await activityEventService.emitUpdate({
          domain: 'workflow',
          event_type: 'hiring_spot.updated',
          actor: {
            user_id: userId,
            user_name: userName,
          },
          subject: {
            resource_type: 'hiring_spot',
            resource_id: spotId,
            resource_name: currentSpot.role,
          },
          message: `Updated hiring spot '${currentSpot.role}'${currentSpot.team_name ? ` in ${currentSpot.team_name}` : ''}`,
          context: {
            program_id: currentSpot.program_id,
            program_name: currentSpot.program_name,
            team_id: currentSpot.team_id,
            team_name: currentSpot.team_name,
          },
          fields_changed: fieldChanges,
          metadata: {
            affected_user_ids: affectedUserIds,
            affected_workflow_ids: validatedUpdates.workflows ?? currentSpot.workflows ?? [],
            affected_employee_ids: [],
            tags: ['workflow', 'hiring_spot', 'updated', 'capacity'],
            visibility: visibility,
          },
        });
      } catch (eventError) {
        logger.error('Failed to emit hiring_spot.updated event', eventError, { spotId });
        // Don't throw - event emission failure shouldn't break the main operation
      }
    } catch (error) {
      logger.error('Failed to update hiring spot', error, { spotId });
      throw error;
    }
  }

  /**
   * Delete a hiring spot
   */
  async deleteHiringSpot(
    spotId: string,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      logger.info('Deleting hiring spot', { spotId });

      await this.firestoreService.delete(spotId, {
        userId,
        userName,
        action: 'delete',
        resourceType: 'hiring_spot',
        resourceId: spotId,
        cycleId: auditContext.cycleId,
      });

      logger.info('Hiring spot deleted successfully', { spotId });
    } catch (error) {
      logger.error('Failed to delete hiring spot', error, { spotId });
      throw error;
    }
  }

  // ============================================================================
  // APPROVAL WORKFLOW METHODS
  // ============================================================================

  /**
   * Submit hiring spot for approval
   */
  async submitForApproval(
    spotId: string,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      logger.info('Submitting hiring spot for approval', { spotId });

      const currentSpot = await this.firestoreService.getRequired(spotId);

      const now = Timestamp.now();

      // Create approval history entry
      const historyEntry: ApprovalHistoryEntry = {
        timestamp: now,
        action: 'submitted',
        actor_id: userId,
        actor_name: userName,
      };

      // Create conversation entry
      const conversationEntry: ConversationEntry = {
        id: `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`,
        author_id: userId,
        author_name: userName,
        message: 'Submitted for approval',
        timestamp: now,
        message_type: 'approval_request',
        metadata: {
          previous_status: currentSpot.approval_status,
          new_status: 'pending_approval',
        },
      };

      const updateData: Partial<HiringSpot> = {
        approval_status: 'pending_approval',
        approval_metadata: {
          ...currentSpot.approval_metadata,
          submitted_at: now,
          submitted_by: userId,
          submitted_by_name: userName,
          approval_history: [...currentSpot.approval_metadata.approval_history, historyEntry],
        },
        notes: {
          ...currentSpot.notes,
          conversation_history: [...currentSpot.notes.conversation_history, conversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
        },
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'hiring_spot',
        resourceId: spotId,
        changes: { approval_status: 'pending_approval' },
        cycleId: auditContext.cycleId,
      });

      logger.info('Hiring spot submitted for approval successfully', { spotId });
    } catch (error) {
      logger.error('Failed to submit hiring spot for approval', error, { spotId });
      throw error;
    }
  }

  /**
   * Approve hiring spot
   */
  async approveHiringSpot(
    spotId: string,
    reviewNotes: string,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      logger.info('Approving hiring spot', { spotId });

      const currentSpot = await this.firestoreService.getRequired(spotId);

      if (currentSpot.approval_status !== 'pending_approval') {
        throw new Error(`Cannot approve hiring spot with status: ${currentSpot.approval_status}`);
      }

      const now = Timestamp.now();

      // Create approval history entry
      const historyEntry: ApprovalHistoryEntry = {
        timestamp: now,
        action: 'approved',
        actor_id: userId,
        actor_name: userName,
        notes: reviewNotes,
      };

      // Create conversation entry
      const conversationEntry: ConversationEntry = {
        id: `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`,
        author_id: userId,
        author_name: userName,
        message: `Approved: ${reviewNotes}`,
        timestamp: now,
        message_type: 'status_change',
        metadata: {
          previous_status: currentSpot.approval_status,
          new_status: 'approved',
        },
      };

      const updateData: Partial<HiringSpot> = {
        approval_status: 'approved',
        approval_metadata: {
          ...currentSpot.approval_metadata,
          reviewed_at: now,
          reviewed_by: userId,
          reviewed_by_name: userName,
          review_notes: reviewNotes,
          approval_history: [...currentSpot.approval_metadata.approval_history, historyEntry],
        },
        notes: {
          ...currentSpot.notes,
          conversation_history: [...currentSpot.notes.conversation_history, conversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
        },
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'hiring_spot',
        resourceId: spotId,
        changes: { approval_status: 'approved' },
        cycleId: auditContext.cycleId,
      });

      logger.info('Hiring spot approved successfully', { spotId });

      // Emit activity event for hiring spot approval
      try {
        // Build field changes including all spot metadata
        const fieldChanges = [
          {
            field_name: 'approval_status',
            old_value: currentSpot.approval_status,
            new_value: 'approved',
            display_name: 'Approval Status'
          },
          ...buildHiringSpotFieldChanges(currentSpot, currentSpot), // Include all current fields as context
        ];

        // Build affected user IDs
        const affectedUserIds = [
          userId,
          currentSpot.created_by,
          currentSpot.hiring_manager,
          ...(currentSpot.trainer ? [currentSpot.trainer] : []),
          ...(currentSpot.collaborators ?? []),
        ].filter((id, index, self) => id && self.indexOf(id) === index);

        await activityEventService.emit({
          domain: 'workflow',
          event_type: 'hiring_spot.approved',
          actor: {
            user_id: userId,
            user_name: userName,
          },
          subject: {
            resource_type: 'hiring_spot',
            resource_id: spotId,
            resource_name: currentSpot.role,
          },
          context: {
            program_id: currentSpot.program_id,
            program_name: currentSpot.program_name,
            team_id: currentSpot.team_id,
            team_name: currentSpot.team_name,
          },
          change: {
            action: 'approved',
            fields_changed: fieldChanges,
            previous_state: {
              approval_status: currentSpot.approval_status,
            },
            new_state: {
              approval_status: 'approved',
              review_notes: reviewNotes,
              reviewed_by: userName,
            },
          },
          message: `Approved hiring spot '${currentSpot.role}'${currentSpot.team_name ? ` in ${currentSpot.team_name}` : ''}`,
          metadata: {
            affected_user_ids: affectedUserIds,
            affected_workflow_ids: currentSpot.workflows ?? [],
            affected_employee_ids: [],
            tags: ['workflow', 'hiring_spot', 'approved', 'status_change', 'capacity'],
            visibility: 'program',
          },
        });
      } catch (eventError) {
        logger.error('Failed to emit hiring_spot.approved event', eventError, { spotId });
        // Don't throw - event emission failure shouldn't break the main operation
      }
    } catch (error) {
      logger.error('Failed to approve hiring spot', error, { spotId });
      throw error;
    }
  }

  /**
   * Mark hiring spot as filled
   */
  async fillHiringSpot(
    spotId: string,
    fillNotes: string,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      logger.info('Marking hiring spot as filled', { spotId });

      const currentSpot = await this.firestoreService.getRequired(spotId);

      if (currentSpot.approval_status !== 'approved') {
        throw new Error(`Cannot mark hiring spot as filled with status: ${currentSpot.approval_status}. Must be approved first.`);
      }

      const now = Timestamp.now();

      // Create approval history entry
      const historyEntry: ApprovalHistoryEntry = {
        timestamp: now,
        action: 'filled',
        actor_id: userId,
        actor_name: userName,
        notes: fillNotes,
      };

      // Create conversation entry
      const conversationEntry: ConversationEntry = {
        id: `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`,
        author_id: userId,
        author_name: userName,
        message: `Marked as filled: ${fillNotes}`,
        timestamp: now,
        message_type: 'status_change',
        metadata: {
          previous_status: currentSpot.approval_status,
          new_status: 'filled',
        },
      };

      const updateData: Partial<HiringSpot> = {
        approval_status: 'filled',
        approval_metadata: {
          ...currentSpot.approval_metadata,
          approval_history: [...currentSpot.approval_metadata.approval_history, historyEntry],
        },
        notes: {
          ...currentSpot.notes,
          conversation_history: [...currentSpot.notes.conversation_history, conversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
        },
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'hiring_spot',
        resourceId: spotId,
        changes: { approval_status: 'filled' },
        cycleId: auditContext.cycleId,
      });

      logger.info('Hiring spot marked as filled successfully', { spotId });

      // Emit activity event for hiring spot filled
      try {
        // Build field changes including all spot metadata
        const fieldChanges = [
          {
            field_name: 'approval_status',
            old_value: currentSpot.approval_status,
            new_value: 'filled',
            display_name: 'Approval Status'
          },
          ...buildHiringSpotFieldChanges(currentSpot, currentSpot), // Include all current fields as context
        ];

        // Build affected user IDs
        const affectedUserIds = [
          userId,
          currentSpot.created_by,
          currentSpot.hiring_manager,
          ...(currentSpot.trainer ? [currentSpot.trainer] : []),
          ...(currentSpot.collaborators ?? []),
        ].filter((id, index, self) => id && self.indexOf(id) === index);

        await activityEventService.emit({
          domain: 'workflow',
          event_type: 'hiring_spot.filled',
          actor: {
            user_id: userId,
            user_name: userName,
          },
          subject: {
            resource_type: 'hiring_spot',
            resource_id: spotId,
            resource_name: currentSpot.role,
          },
          context: {
            program_id: currentSpot.program_id,
            program_name: currentSpot.program_name,
            team_id: currentSpot.team_id,
            team_name: currentSpot.team_name,
          },
          change: {
            action: 'filled',
            fields_changed: fieldChanges,
            previous_state: {
              approval_status: currentSpot.approval_status,
            },
            new_state: {
              approval_status: 'filled',
              fill_notes: fillNotes,
              filled_by: userName,
            },
          },
          message: `Filled '${currentSpot.role}' hiring spot in ${currentSpot.team_name || 'team'}`,
          metadata: {
            affected_user_ids: affectedUserIds,
            affected_workflow_ids: currentSpot.workflows ?? [],
            affected_employee_ids: [],
            tags: ['workflow', 'hiring_spot', 'filled', 'status_change', 'capacity'],
            visibility: 'program',
          },
        });
      } catch (eventError) {
        logger.error('Failed to emit hiring_spot.filled event', eventError, { spotId });
        // Don't throw - event emission failure shouldn't break the main operation
      }
    } catch (error) {
      logger.error('Failed to mark hiring spot as filled', error, { spotId });
      throw error;
    }
  }

  /**
   * Archive hiring spot (hide from active view)
   */
  async archiveHiringSpot(
    spotId: string,
    archiveNotes: string,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      logger.info('Archiving hiring spot', { spotId });

      const currentSpot = await this.firestoreService.getRequired(spotId);

      const now = Timestamp.now();

      // Create approval history entry
      const historyEntry: ApprovalHistoryEntry = {
        timestamp: now,
        action: 'archived',
        actor_id: userId,
        actor_name: userName,
        notes: archiveNotes,
      };

      // Create conversation entry
      const conversationEntry: ConversationEntry = {
        id: `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`,
        author_id: userId,
        author_name: userName,
        message: `Archived: ${archiveNotes}`,
        timestamp: now,
        message_type: 'status_change',
        metadata: {},
      };

      const updateData: Partial<HiringSpot> = {
        is_archived: true,
        approval_metadata: {
          ...currentSpot.approval_metadata,
          reviewed_at: now,
          reviewed_by: userId,
          reviewed_by_name: userName,
          review_notes: archiveNotes,
          approval_history: [...currentSpot.approval_metadata.approval_history, historyEntry],
        },
        notes: {
          ...currentSpot.notes,
          conversation_history: [...currentSpot.notes.conversation_history, conversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
        },
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'hiring_spot',
        resourceId: spotId,
        changes: { is_archived: true },
        cycleId: auditContext.cycleId,
      });

      logger.info('Hiring spot archived successfully', { spotId });

      // Emit activity event for hiring spot archival
      try {
        // Build field changes including all spot metadata
        const fieldChanges = [
          {
            field_name: 'is_archived',
            old_value: false,
            new_value: true,
            display_name: 'Archived Status'
          },
          ...buildHiringSpotFieldChanges(currentSpot, currentSpot), // Include all current fields as context
        ];

        // Build affected user IDs
        const affectedUserIds = [
          userId,
          currentSpot.created_by,
          currentSpot.hiring_manager,
          ...(currentSpot.trainer ? [currentSpot.trainer] : []),
          ...(currentSpot.collaborators ?? []),
        ].filter((id, index, self) => id && self.indexOf(id) === index);

        await activityEventService.emit({
          domain: 'workflow',
          event_type: 'hiring_spot.archived',
          actor: {
            user_id: userId,
            user_name: userName,
          },
          subject: {
            resource_type: 'hiring_spot',
            resource_id: spotId,
            resource_name: currentSpot.role,
          },
          context: {
            program_id: currentSpot.program_id,
            program_name: currentSpot.program_name,
            team_id: currentSpot.team_id,
            team_name: currentSpot.team_name,
          },
          change: {
            action: 'archived',
            fields_changed: fieldChanges,
            previous_state: {
              is_archived: currentSpot.is_archived,
              approval_status: currentSpot.approval_status,
            },
            new_state: {
              is_archived: true,
              archive_notes: archiveNotes,
              archived_by: userName,
            },
          },
          message: `Archived hiring spot '${currentSpot.role}'${currentSpot.team_name ? ` in ${currentSpot.team_name}` : ''}`,
          metadata: {
            affected_user_ids: affectedUserIds,
            affected_workflow_ids: currentSpot.workflows ?? [],
            affected_employee_ids: [],
            tags: ['workflow', 'hiring_spot', 'archived', 'status_change', 'capacity'],
            visibility: 'program',
          },
        });
      } catch (eventError) {
        logger.error('Failed to emit hiring_spot.archived event', eventError, { spotId });
        // Don't throw - event emission failure shouldn't break the main operation
      }
    } catch (error) {
      logger.error('Failed to archive hiring spot', error, { spotId });
      throw error;
    }
  }

  /**
   * Unarchive hiring spot (restore to active view)
   */
  async unarchiveHiringSpot(
    spotId: string,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      logger.info('Unarchiving hiring spot', { spotId });

      const currentSpot = await this.firestoreService.getRequired(spotId);

      const now = Timestamp.now();

      // Create approval history entry
      const historyEntry: ApprovalHistoryEntry = {
        timestamp: now,
        action: 'unarchived',
        actor_id: userId,
        actor_name: userName,
      };

      // Create conversation entry
      const conversationEntry: ConversationEntry = {
        id: `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`,
        author_id: userId,
        author_name: userName,
        message: 'Unarchived',
        timestamp: now,
        message_type: 'status_change',
        metadata: {},
      };

      const updateData: Partial<HiringSpot> = {
        is_archived: false,
        approval_metadata: {
          ...currentSpot.approval_metadata,
          approval_history: [...currentSpot.approval_metadata.approval_history, historyEntry],
        },
        notes: {
          ...currentSpot.notes,
          conversation_history: [...currentSpot.notes.conversation_history, conversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
        },
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'hiring_spot',
        resourceId: spotId,
        changes: { is_archived: false },
        cycleId: auditContext.cycleId,
      });

      logger.info('Hiring spot unarchived successfully', { spotId });
    } catch (error) {
      logger.error('Failed to unarchive hiring spot', error, { spotId });
      throw error;
    }
  }

  /**
   * Close hiring spot (retire a filled spot from active planning views)
   *
   * Unlike archiving (a soft-delete that hides the spot everywhere), closing
   * removes the spot from active/forward-looking views while keeping it visible
   * in the Hiring Placement board as the historical placement record. Used to
   * clear the now-duplicate placeholder once the hire has started.
   */
  async closeHiringSpot(
    spotId: string,
    closeNotes: string,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      logger.info('Closing hiring spot', { spotId });

      const currentSpot = await this.firestoreService.getRequired(spotId);

      if (currentSpot.approval_status !== 'filled') {
        throw new Error(`Cannot close hiring spot with status: ${currentSpot.approval_status}. Must be filled first.`);
      }
      if (currentSpot.is_archived) {
        throw new Error('Cannot close an archived hiring spot. Unarchive it first.');
      }

      const now = Timestamp.now();

      const historyEntry: ApprovalHistoryEntry = {
        timestamp: now,
        action: 'closed',
        actor_id: userId,
        actor_name: userName,
        notes: closeNotes,
      };

      const conversationEntry: ConversationEntry = {
        id: `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`,
        author_id: userId,
        author_name: userName,
        message: `Closed: ${closeNotes}`,
        timestamp: now,
        message_type: 'status_change',
        metadata: {},
      };

      const updateData: Partial<HiringSpot> = {
        is_closed: true,
        approval_metadata: {
          ...currentSpot.approval_metadata,
          approval_history: [...currentSpot.approval_metadata.approval_history, historyEntry],
        },
        notes: {
          ...currentSpot.notes,
          conversation_history: [...currentSpot.notes.conversation_history, conversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
        },
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'hiring_spot',
        resourceId: spotId,
        changes: { is_closed: true },
        cycleId: auditContext.cycleId,
      });

      logger.info('Hiring spot closed successfully', { spotId });

      // Emit activity event for hiring spot closure (audit-only; not surfaced in
      // the homepage feed, matching the archived-event precedent)
      try {
        await activityEventService.emit({
          domain: 'workflow',
          event_type: 'hiring_spot.closed',
          actor: {
            user_id: userId,
            user_name: userName,
          },
          subject: {
            resource_type: 'hiring_spot',
            resource_id: spotId,
            resource_name: currentSpot.role,
          },
          context: {
            program_id: currentSpot.program_id,
            program_name: currentSpot.program_name,
            team_id: currentSpot.team_id,
            team_name: currentSpot.team_name,
          },
          change: {
            action: 'closed',
            fields_changed: [
              {
                field_name: 'is_closed',
                old_value: false,
                new_value: true,
                display_name: 'Closed Status',
              },
            ],
            previous_state: { is_closed: currentSpot.is_closed ?? false },
            new_state: { is_closed: true, close_notes: closeNotes, closed_by: userName },
          },
          message: `Closed hiring spot '${currentSpot.role}'${currentSpot.team_name ? ` in ${currentSpot.team_name}` : ''}`,
          metadata: {
            affected_user_ids: [
              userId,
              currentSpot.created_by,
              currentSpot.hiring_manager,
              ...(currentSpot.trainer ? [currentSpot.trainer] : []),
              ...(currentSpot.collaborators ?? []),
            ].filter((id, index, self) => id && self.indexOf(id) === index),
            affected_workflow_ids: currentSpot.workflows ?? [],
            affected_employee_ids: [],
            tags: ['workflow', 'hiring_spot', 'closed', 'status_change', 'capacity'],
            visibility: 'program',
          },
        });
      } catch (eventError) {
        logger.error('Failed to emit hiring_spot.closed event', eventError, { spotId });
        // Don't throw - event emission failure shouldn't break the main operation
      }
    } catch (error) {
      logger.error('Failed to close hiring spot', error, { spotId });
      throw error;
    }
  }

  /**
   * Reopen hiring spot (restore a closed spot to active planning views)
   */
  async reopenHiringSpot(
    spotId: string,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      logger.info('Reopening hiring spot', { spotId });

      const currentSpot = await this.firestoreService.getRequired(spotId);

      const now = Timestamp.now();

      const historyEntry: ApprovalHistoryEntry = {
        timestamp: now,
        action: 'reopened',
        actor_id: userId,
        actor_name: userName,
      };

      const conversationEntry: ConversationEntry = {
        id: `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`,
        author_id: userId,
        author_name: userName,
        message: 'Reopened',
        timestamp: now,
        message_type: 'status_change',
        metadata: {},
      };

      const updateData: Partial<HiringSpot> = {
        is_closed: false,
        approval_metadata: {
          ...currentSpot.approval_metadata,
          approval_history: [...currentSpot.approval_metadata.approval_history, historyEntry],
        },
        notes: {
          ...currentSpot.notes,
          conversation_history: [...currentSpot.notes.conversation_history, conversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
        },
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'hiring_spot',
        resourceId: spotId,
        changes: { is_closed: false },
        cycleId: auditContext.cycleId,
      });

      logger.info('Hiring spot reopened successfully', { spotId });

      try {
        await activityEventService.emit({
          domain: 'workflow',
          event_type: 'hiring_spot.reopened',
          actor: {
            user_id: userId,
            user_name: userName,
          },
          subject: {
            resource_type: 'hiring_spot',
            resource_id: spotId,
            resource_name: currentSpot.role,
          },
          context: {
            program_id: currentSpot.program_id,
            program_name: currentSpot.program_name,
            team_id: currentSpot.team_id,
            team_name: currentSpot.team_name,
          },
          change: {
            action: 'reopened',
            fields_changed: [
              {
                field_name: 'is_closed',
                old_value: true,
                new_value: false,
                display_name: 'Closed Status',
              },
            ],
            previous_state: { is_closed: currentSpot.is_closed ?? false },
            new_state: { is_closed: false, reopened_by: userName },
          },
          message: `Reopened hiring spot '${currentSpot.role}'${currentSpot.team_name ? ` in ${currentSpot.team_name}` : ''}`,
          metadata: {
            affected_user_ids: [
              userId,
              currentSpot.created_by,
              currentSpot.hiring_manager,
              ...(currentSpot.trainer ? [currentSpot.trainer] : []),
              ...(currentSpot.collaborators ?? []),
            ].filter((id, index, self) => id && self.indexOf(id) === index),
            affected_workflow_ids: currentSpot.workflows ?? [],
            affected_employee_ids: [],
            tags: ['workflow', 'hiring_spot', 'reopened', 'status_change', 'capacity'],
            visibility: 'program',
          },
        });
      } catch (eventError) {
        logger.error('Failed to emit hiring_spot.reopened event', eventError, { spotId });
        // Don't throw - event emission failure shouldn't break the main operation
      }
    } catch (error) {
      logger.error('Failed to reopen hiring spot', error, { spotId });
      throw error;
    }
  }

  // ============================================================================
  // CONFIRMED HIRE LINKING METHODS
  // ============================================================================

  /**
   * Link a confirmed hire to a hiring spot (bi-directional)
   */
  async linkConfirmedHire(
    spotId: string,
    confirmedHireId: string,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      logger.info('Linking confirmed hire to hiring spot', { spotId, confirmedHireId });

      const currentSpot = await this.firestoreService.getRequired(spotId);

      // Check if spot is already linked to another hire
      if (currentSpot.confirmed_hire_id && currentSpot.confirmed_hire_id !== confirmedHireId) {
        throw new Error(`Hiring spot is already linked to confirmed hire: ${currentSpot.confirmed_hire_id}`);
      }

      const now = Timestamp.now();

      // Create conversation entry
      const conversationEntry: ConversationEntry = {
        id: `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`,
        author_id: userId,
        author_name: userName,
        message: `Linked to confirmed hire: ${confirmedHireId}`,
        timestamp: now,
        message_type: 'status_change',
        metadata: {},
      };

      const updateData: Partial<HiringSpot> = {
        confirmed_hire_id: confirmedHireId,
        notes: {
          ...currentSpot.notes,
          conversation_history: [...currentSpot.notes.conversation_history, conversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
        },
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'hiring_spot',
        resourceId: spotId,
        changes: { confirmed_hire_id: confirmedHireId },
        cycleId: auditContext.cycleId,
      });

      logger.info('Confirmed hire linked successfully', { spotId, confirmedHireId });
    } catch (error) {
      logger.error('Failed to link confirmed hire', error, { spotId, confirmedHireId });
      throw error;
    }
  }

  /**
   * Unlink a confirmed hire from a hiring spot
   */
  async unlinkConfirmedHire(
    spotId: string,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      logger.info('Unlinking confirmed hire from hiring spot', { spotId });

      const currentSpot = await this.firestoreService.getRequired(spotId);

      if (!currentSpot.confirmed_hire_id) {
        logger.warn('Hiring spot has no linked confirmed hire', { spotId });
        return; // Nothing to unlink
      }

      const now = Timestamp.now();
      const previousHireId = currentSpot.confirmed_hire_id;

      // Create conversation entry
      const conversationEntry: ConversationEntry = {
        id: `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`,
        author_id: userId,
        author_name: userName,
        message: `Unlinked from confirmed hire: ${previousHireId}`,
        timestamp: now,
        message_type: 'status_change',
        metadata: {},
      };

      const updateData: Partial<HiringSpot> = {
        confirmed_hire_id: null,
        notes: {
          ...currentSpot.notes,
          conversation_history: [...currentSpot.notes.conversation_history, conversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
        },
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'hiring_spot',
        resourceId: spotId,
        changes: { confirmed_hire_id: null },
        cycleId: auditContext.cycleId,
      });

      logger.info('Confirmed hire unlinked successfully', { spotId, previousHireId });
    } catch (error) {
      logger.error('Failed to unlink confirmed hire', error, { spotId });
      throw error;
    }
  }

  /**
   * Revert hiring spot to previous status
   * - From 'filled' -> 'approved'
   * - From 'approved' -> 'pending_approval'
   */
  async revertHiringSpotStatus(
    spotId: string,
    revertNotes: string,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      logger.info('Reverting hiring spot status', { spotId });

      const currentSpot = await this.firestoreService.getRequired(spotId);

      // Determine previous status based on current status
      let previousStatus: ApprovalStatus;
      if (currentSpot.approval_status === 'filled') {
        previousStatus = 'approved';
      } else if (currentSpot.approval_status === 'approved') {
        previousStatus = 'pending_approval';
      } else {
        throw new Error(`Cannot revert hiring spot with status: ${currentSpot.approval_status}`);
      }

      const now = Timestamp.now();

      // Create approval history entry
      const historyEntry: ApprovalHistoryEntry = {
        timestamp: now,
        action: 'submitted', // Using 'submitted' to track the revert action
        actor_id: userId,
        actor_name: userName,
        notes: `Reverted from ${currentSpot.approval_status} to ${previousStatus}: ${revertNotes}`,
      };

      // Create conversation entry
      const conversationEntry: ConversationEntry = {
        id: `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`,
        author_id: userId,
        author_name: userName,
        message: `Status reverted from ${currentSpot.approval_status} to ${previousStatus}: ${revertNotes}`,
        timestamp: now,
        message_type: 'status_change',
        metadata: {
          previous_status: currentSpot.approval_status,
          new_status: previousStatus,
        },
      };

      const updateData: Partial<HiringSpot> = {
        approval_status: previousStatus,
        approval_metadata: {
          ...currentSpot.approval_metadata,
          approval_history: [...currentSpot.approval_metadata.approval_history, historyEntry],
        },
        notes: {
          ...currentSpot.notes,
          conversation_history: [...currentSpot.notes.conversation_history, conversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
        },
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'hiring_spot',
        resourceId: spotId,
        changes: { approval_status: previousStatus, reverted: true },
        cycleId: auditContext.cycleId,
      });

      logger.info('Hiring spot status reverted successfully', { spotId, from: currentSpot.approval_status, to: previousStatus });
    } catch (error) {
      logger.error('Failed to revert hiring spot status', error, { spotId });
      throw error;
    }
  }


  // ============================================================================
  // CONVERSATION MANAGEMENT
  // ============================================================================

  /**
   * Add conversation entry to hiring spot
   */
  async addConversationEntry(
    spotId: string,
    entry: AddConversationEntryData,
    userId: string,
    userName: string,
    auditContext: AuditContext
  ): Promise<void> {
    try {
      logger.info('Adding conversation entry to hiring spot', { spotId });

      // Validate input data
      const validatedEntry = AddConversationEntryDataSchema.parse(entry);

      const currentSpot = await this.firestoreService.getRequired(spotId);
      const now = Timestamp.now();

      // Create conversation entry - only include metadata if it exists
      const conversationEntry: ConversationEntry = {
        id: `entry-${now.toMillis()}-${Math.random().toString(36).substr(2, 9)}`,
        author_id: userId,
        author_name: userName,
        message: validatedEntry.message,
        timestamp: now,
        message_type: validatedEntry.message_type,
        ...(validatedEntry.metadata && { metadata: validatedEntry.metadata }),
      };

      const updateData: Partial<HiringSpot> = {
        notes: {
          ...currentSpot.notes,
          conversation_history: [...currentSpot.notes.conversation_history, conversationEntry],
          last_updated: now,
          last_updated_by: userId,
          last_updated_by_name: userName,
        },
        last_modified_by: userId,
        last_modified_by_name: userName,
        version: currentSpot.version + 1,
      };

      await this.firestoreService.update(spotId, updateData, {
        userId,
        userName,
        action: 'update',
        resourceType: 'hiring_spot',
        resourceId: spotId,
        changes: { conversation_entry_added: true },
        cycleId: auditContext.cycleId,
      });

      logger.info('Conversation entry added successfully', { spotId });

      // Note: hiring_spot.commented event removed as per requirements
      // Comments are now tracked within the hiring spot's conversation history
    } catch (error) {
      logger.error('Failed to add conversation entry', error, { spotId });
      throw error;
    }
  }
}

// Export singleton instance
export const hiringSpotService = new HiringSpotService();
